import React from 'react';
import { AlertTriangle, Cpu, TrendingUp, Shield, Zap, Calendar, Wrench, Clock, Target, Users, BarChart3, Settings, DollarSign, Hammer } from 'lucide-react';
import { ComposedChart, Bar, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import { useDashboard } from '../../DashboardContext';
import { S, SCENARIO_LABELS, annualizedROI } from '../../lib/constants';
import SortedTooltip from '../shared/SortedTooltip';
import ChartLegend from '../shared/ChartLegend';

/* ── Shared chart axis config ── */
const axisProps = {
  stroke: 'transparent', axisLine: false, tickLine: false,
  tick: { fontSize: 10, fill: '#737373', fontFamily: "'IBM Plex Mono', monospace" },
};
const gridProps = { vertical: false, strokeDasharray: '3 6', stroke: '#E5E5E5' };

/* ── Operational task card ── */
const OpsTask = ({ icon: Icon, title, description, frequency, color }) => (
  <div style={{ background: '#FFFFFF', border: '1px solid #E5E5E5', borderRadius: '8px', padding: '16px', position: 'relative', overflow: 'hidden' }}>
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: color }} />
    <div className="flex items-start gap-3">
      <div style={{ width: 36, height: 36, borderRadius: '4px', background: `${color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon style={{ width: 18, height: 18, color }} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#1A1A1A', marginBottom: '2px', fontFamily: "'Outfit', sans-serif" }}>{title}</div>
        <div style={{ fontSize: '11px', color: '#525252', lineHeight: 1.5, fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}>{description}</div>
        <div style={{ fontSize: '10px', color: '#737373', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: "'IBM Plex Mono', monospace" }}>{frequency}</div>
      </div>
    </div>
  </div>
);

export default function AnalyticsSection() {
  const {
    activeStrategy,
    activeCalc,
    miningEconomics,
    sensitivityAnalysis,
    timeHorizon,
    btcPrice,
    priceScenario,
    electricityCost,
    fmt, fBTC,
  } = useDashboard();

  const annualData = activeCalc?.annualData || [];
  const strat = activeStrategy;
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const totalMiners = strat.startingMiners + (strat.expansions || []).reduce((s, e) => s + (e.miners || 0), 0);
  const expansionCount = (strat.expansions || []).filter(e => e.miners > 0).length;
  const hasLoans = strat.useInitialFinancing || (strat.expansions || []).some(e => e.useLoan);

  const annualChartData = annualData.map(r => ({
    year: `Yr ${r.year}`,
    revenue: r.btcMinedValue || 0,
    opEx: r.opEx || 0,
    loanPayments: r.loanPayments || 0,
    capEx: r.capexThisYear || 0,
    cash: r.cash || 0,
    nav: r.nav || 0,
    netProfit: r.netProfit || 0,
    btcMined: r.btcMined || 0,
    treasuryBTC: r.treasuryBTC || 0,
    btcPrice: r.btcPrice || 0,
    roi: r.roi || 0,
    miners: r.minersEOY || 0,
    resale: r.resaleProceeds || 0,
  }));

  if (!activeCalc) {
    return (
      <div className="text-sm italic p-4" style={{ color: '#737373' }}>
        Set up this plan's configuration to generate the briefing.
      </div>
    );
  }

  const finalNAV = activeCalc.finalNAV || 0;
  const finalTreasury = activeCalc.finalTreasury || 0;
  const finalROI = activeCalc.finalROI || 0;
  const mult = miningEconomics?.miningMultiple ?? (activeCalc.miningMultiplier || 0);
  const grossMult = miningEconomics?.grossMultiple ?? mult;
  const breakEven = activeCalc.breakEvenMonth;
  const roiPerYear = annualizedROI(finalROI, timeHorizon);
  const scenarioLabel = SCENARIO_LABELS[priceScenario] || '';
  const hasTaxOnMult = (miningEconomics?.taxSavings || 0) > 0 && mult !== grossMult;

  return (
    <div className="space-y-6">

      {/* ═══════ HERO BRIEFING HEADER ═══════ */}
      <div style={{ background: '#FFFFFF', borderRadius: '8px', overflow: 'hidden', borderTop: '1px solid #E5E5E5', borderRight: '1px solid #E5E5E5', borderBottom: '1px solid #E5E5E5', borderLeft: '6px solid #92700C' }}>
        <div style={{ padding: '36px 40px 28px' }}>
          <div className="flex items-start justify-between">
            <div>
              <div style={{ fontSize: '12px', color: '#92700C', textTransform: 'uppercase', letterSpacing: '2px', fontWeight: 600, marginBottom: '8px', fontFamily: "'IBM Plex Mono', monospace" }}>Mining Strategy Briefing</div>
              <div style={{ fontSize: '28px', fontWeight: 600, color: '#1A1A1A', letterSpacing: '-0.02em', lineHeight: 1.2, fontFamily: "'Outfit', sans-serif" }}>{strat.name}</div>
              <div style={{ fontSize: '14px', color: '#525252', marginTop: '8px', fontWeight: 300, fontFamily: "'Outfit', sans-serif" }}>
                {timeHorizon}-year projection | {totalMiners} miners | {expansionCount} expansion{expansionCount !== 1 ? 's' : ''} | BTC @ {fmt(btcPrice)} today
              </div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: '10px', color: '#525252', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'IBM Plex Mono', monospace" }}>Prepared</div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#333333', marginTop: '2px', fontFamily: "'IBM Plex Mono', monospace" }}>{today}</div>
              <div style={{ fontSize: '20px', fontWeight: 600, color: '#92700C', marginTop: '8px', letterSpacing: '-0.01em', fontFamily: "'Outfit', sans-serif" }}>MinerOps</div>
            </div>
          </div>
        </div>

        {/* Hero KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" style={{ padding: '0 40px 32px' }}>
          {[
            { label: 'Projected Net Asset Value', value: fmt(finalNAV), color: '#2563EB', sub: `Year ${timeHorizon}` },
            { label: 'BTC Treasury', value: `${fBTC(finalTreasury, 2)} BTC`, color: '#92700C', sub: 'Total accumulated' },
            { label: 'Total Return on Investment', value: `${finalROI.toFixed(0)}%`, color: '#16A34A', sub: roiPerYear !== null ? `≈ ${roiPerYear.toFixed(0)}%/yr · ${scenarioLabel} price` : `Over ${timeHorizon} yrs · ${scenarioLabel} price` },
            { label: 'Mining Multiple', value: `${mult.toFixed(2)}x`, color: mult >= 1 ? '#92700C' : '#DC2626', sub: hasTaxOnMult ? `After write-offs · ${grossMult.toFixed(2)}x before` : 'Revenue vs all-in cost' },
          ].map((kpi, i) => (
            <div key={i} style={{ background: '#F5F5F5', border: '1px solid #E5E5E5', borderRadius: '4px', padding: '18px 20px' }}>
              <div style={{ fontSize: '10px', color: '#525252', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px', fontFamily: "'IBM Plex Mono', monospace" }}>{kpi.label}</div>
              <div style={{ fontSize: '26px', fontWeight: 600, color: kpi.color, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1, fontFamily: "'IBM Plex Mono', monospace" }}>{kpi.value}</div>
              <div style={{ fontSize: '11px', color: '#525252', marginTop: '4px', fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}>{kpi.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ═══════ EXECUTIVE SUMMARY ═══════ */}
      <div className="overflow-hidden" style={{ ...S.card, borderLeft: '4px solid #92700C' }}>
        <div className="p-6">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-4 h-4" style={{ color: '#92700C' }} />
            <h3 className="text-sm" style={{ color: '#1A1A1A', fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>Executive Summary</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
            {[
              { l: 'Fleet', v: expansionCount > 0 ? `${strat.startingMiners} to ${totalMiners} miners` : `${strat.startingMiners} miners`, s: strat.initialCapexIsSunk ? 'existing fleet, sunk cost' : `${fmt(strat.startingMiners * strat.minerPrice)} initial CapEx${hasLoans ? ', financed' : ''}` },
              { l: 'Production', v: `${fBTC(activeCalc.totalBTCMined, 2)} BTC`, s: `over ${timeHorizon} years` },
              { l: 'Cost per BTC', v: miningEconomics ? fmt(miningEconomics.netCostPerBTC) : '—', s: miningEconomics && miningEconomics.savingsPercent > 0 ? `${miningEconomics.savingsPercent.toFixed(0)}% below ${fmt(miningEconomics.avgSpotPrice)} avg spot` : 'after tax deductions' },
              { l: 'Break-even', v: breakEven ? `Month ${breakEven}` : 'Not reached', s: expansionCount > 0 ? `${expansionCount} phased expansion${expansionCount > 1 ? 's' : ''}` : 'no expansions planned' },
            ].map((f, i) => (
              <div key={i}>
                <div style={{ fontSize: '10px', color: '#737373', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'IBM Plex Mono', monospace" }}>{f.l}</div>
                <div style={{ fontSize: '16px', fontWeight: 600, color: '#1A1A1A', fontVariantNumeric: 'tabular-nums', marginTop: '2px' }}>{f.v}</div>
                <div style={{ fontSize: '11px', color: '#525252', fontWeight: 300, fontFamily: "'Outfit', sans-serif" }}>{f.s}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══════ MINING ECONOMICS ═══════ */}
      {miningEconomics && (
        <div className="overflow-hidden" style={S.card}>
          <div className="p-5">
            <div className="mb-4">
              <h3 className="text-sm flex items-center gap-2" style={{ color: '#1A1A1A', fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
                <Cpu className="w-4 h-4" style={{ color: S.C.gold }} /> Mining Economics
              </h3>
              <p className="text-xs mt-0.5" style={{ color: '#737373' }}>What you pay per BTC compared to just buying it</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
              {[
                { label: 'Total BTC Mined', value: fBTC(miningEconomics.totalBTCMined, 2) + ' BTC', color: S.C.gold },
                { label: 'Gross Cost/BTC', value: fmt(miningEconomics.grossCostPerBTC), color: S.C.red },
                { label: 'Net Cost/BTC', value: fmt(miningEconomics.netCostPerBTC), color: S.C.green, sub: 'after tax deductions' },
                { label: 'Avg Spot Price', value: fmt(miningEconomics.avgSpotPrice), color: S.C.blue },
                {
                  label: 'Savings vs Spot',
                  value: `${fmt(miningEconomics.savingsVsSpot)}/BTC`,
                  color: miningEconomics.savingsPercent >= 0 ? S.C.green : S.C.red,
                  sub: `${miningEconomics.savingsPercent.toFixed(1)}% ${miningEconomics.savingsPercent >= 0 ? 'cheaper' : 'more expensive'}`,
                  span: true,
                },
              ].map((m, i) => (
                <div key={i} className={m.span ? 'col-span-2' : ''} style={{ ...S.innerCard, padding: '12px' }}>
                  <div style={{ ...S.labelSm, marginBottom: '4px' }}>{m.label}</div>
                  <div className="text-lg font-bold" style={S.tn(m.color)}>{m.value}</div>
                  {m.sub && <div className="text-xs mt-0.5" style={{ color: '#737373' }}>{m.sub}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═══════ NAV GROWTH TRAJECTORY ═══════ */}
      {annualChartData.length > 0 && (
        <div className="overflow-hidden" style={S.card}>
          <div className="p-5">
            <div className="mb-4">
              <h3 className="text-sm flex items-center gap-2" style={{ color: '#1A1A1A', fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
                <TrendingUp className="w-4 h-4" style={{ color: '#2563EB' }} /> Portfolio Growth Trajectory
              </h3>
              <p className="text-xs mt-0.5" style={{ color: '#737373' }}>Revenue, operating costs, and net asset value over the full horizon</p>
            </div>
            <ResponsiveContainer width="100%" height={340}>
              <ComposedChart data={annualChartData} barGap={2}>
                <defs>
                  <linearGradient id="gradNavBriefing" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2563EB" stopOpacity={0.12} />
                    <stop offset="100%" stopColor="#2563EB" stopOpacity={0.01} />
                  </linearGradient>
                </defs>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="year" {...axisProps} interval={0} tickFormatter={(v, i) => (i === 0 ? v : String(v).replace('Yr ', ''))} />
                <YAxis yAxisId="usd" {...axisProps} tickFormatter={fmt} width={65} />
                <YAxis yAxisId="nav" orientation="right" {...axisProps} tickFormatter={fmt} width={65} tick={{ fontSize: 10, fill: '#2563EB', fontFamily: "'IBM Plex Mono', monospace" }} />
                <Tooltip content={<SortedTooltip formatter={fmt} />} />
                {activeCalc.fleetOfflineMonth > 0 && activeCalc.fleetOfflineMonth < timeHorizon * 12 && (
                  <ReferenceLine
                    yAxisId="usd"
                    x={`Yr ${Math.ceil(activeCalc.fleetOfflineMonth / 12)}`}
                    stroke="#92700C" strokeDasharray="4 4"
                    label={{ value: 'Fleet retired', position: 'top', fill: '#92700C', fontSize: 11, fontWeight: 600 }}
                  />
                )}
                <Bar yAxisId="usd" dataKey="revenue" name="Revenue" fill="#16A34A" fillOpacity={0.8} radius={[4,4,0,0]} barSize={18} />
                <Bar yAxisId="usd" dataKey="opEx" name="OpEx" fill="#DC2626" fillOpacity={0.8} radius={[4,4,0,0]} barSize={18} />
                <Bar yAxisId="usd" dataKey="capEx" name="CapEx" fill="#7C3AED" fillOpacity={0.8} radius={[4,4,0,0]} barSize={18} />
                {annualChartData.some(d => d.loanPayments > 0) && (
                  <Bar yAxisId="usd" dataKey="loanPayments" name="Loan Pmts" fill="#92700C" fillOpacity={0.8} radius={[4,4,0,0]} barSize={18} />
                )}
                {annualChartData.some(d => d.resale > 0) && (
                  <Bar yAxisId="usd" dataKey="resale" name="Resale" fill="#0891B2" fillOpacity={0.8} radius={[4,4,0,0]} barSize={18} />
                )}
                <Area yAxisId="nav" type="monotone" dataKey="nav" name="NAV" stroke="#2563EB" strokeWidth={2} fill="url(#gradNavBriefing)" dot={{ r: 3, fill: '#fff', strokeWidth: 2, stroke: '#2563EB' }} />
                <ReferenceLine yAxisId="usd" y={0} stroke="#737373" strokeDasharray="3 3" />
              </ComposedChart>
            </ResponsiveContainer>
            <ChartLegend items={[
              { name: 'Revenue', color: '#16A34A' },
              { name: 'OpEx', color: '#DC2626' },
              { name: 'CapEx', color: '#7C3AED' },
              ...(annualChartData.some(d => d.loanPayments > 0) ? [{ name: 'Loan Pmts', color: '#92700C' }] : []),
              ...(annualChartData.some(d => d.resale > 0) ? [{ name: 'Resale', color: '#0891B2' }] : []),
              { name: 'NAV', color: '#2563EB' },
            ]} />
          </div>
        </div>
      )}

      {/* ═══════ BTC ACCUMULATION ═══════ */}
      {annualChartData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="overflow-hidden" style={S.card}>
            <div className="p-5">
              <div className="mb-4">
                <h3 className="text-sm flex items-center gap-2" style={{ color: '#1A1A1A', fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
                  <Hammer className="w-4 h-4" style={{ color: '#92700C' }} /> BTC Production & Treasury
                </h3>
                <p className="text-xs mt-0.5" style={{ color: '#737373' }}>Annual production and cumulative holdings</p>
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={annualChartData}>
                  <defs>
                    <linearGradient id="gradTreasuryBriefing" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#92700C" stopOpacity={0.2} />
                      <stop offset="100%" stopColor="#92700C" stopOpacity={0.01} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="year" {...axisProps} />
                  <YAxis yAxisId="btc" {...axisProps} tickFormatter={v => `${v.toFixed(1)}`} width={45} />
                  <YAxis yAxisId="treasury" orientation="right" {...axisProps} tickFormatter={v => `${v.toFixed(1)}`} width={45} tick={{ fontSize: 10, fill: '#92700C', fontFamily: "'IBM Plex Mono', monospace" }} />
                  <Tooltip content={<SortedTooltip formatter={(v) => fBTC(v, 4) + ' BTC'} />} />
                  <Bar yAxisId="btc" dataKey="btcMined" name="BTC Mined" radius={[4,4,0,0]} barSize={22}>
                    {annualChartData.map((entry, i) => (
                      <Cell key={i} fill={`rgba(184, 134, 11, ${0.4 + (i / annualChartData.length) * 0.5})`} />
                    ))}
                  </Bar>
                  <Area yAxisId="treasury" type="monotone" dataKey="treasuryBTC" name="Treasury" stroke="#92700C" strokeWidth={2} fill="url(#gradTreasuryBriefing)" dot={{ r: 3, fill: '#fff', strokeWidth: 2, stroke: '#92700C' }} />
                </ComposedChart>
              </ResponsiveContainer>
              <ChartLegend items={[
                { name: 'BTC Mined', color: '#92700C' },
                { name: 'Treasury', color: '#92700C', dashed: true },
              ]} />
            </div>
          </div>

          {/* Cash Balance & ROI */}
          <div className="overflow-hidden" style={S.card}>
            <div className="p-5">
              <div className="mb-4">
                <h3 className="text-sm flex items-center gap-2" style={{ color: '#1A1A1A', fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
                  <DollarSign className="w-4 h-4" style={{ color: '#0891B2' }} /> Cash Balance & ROI
                </h3>
                <p className="text-xs mt-0.5" style={{ color: '#737373' }}>Available cash and return on investment</p>
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={annualChartData}>
                  <defs>
                    <linearGradient id="gradCashBriefing" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0891B2" stopOpacity={0.15} />
                      <stop offset="100%" stopColor="#0891B2" stopOpacity={0.01} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid {...gridProps} />
                  <XAxis dataKey="year" {...axisProps} />
                  <YAxis yAxisId="cash" {...axisProps} tickFormatter={fmt} width={65} />
                  <YAxis yAxisId="roi" orientation="right" {...axisProps} tickFormatter={v => `${v.toFixed(0)}%`} width={45} tick={{ fontSize: 10, fill: '#7C3AED', fontFamily: "'IBM Plex Mono', monospace" }} />
                  <Tooltip content={<SortedTooltip formatter={(v, name) => {
                    if (typeof name === 'string' && name.includes('ROI')) return `${v.toFixed(1)}%`;
                    return fmt(v);
                  }} />} />
                  <Area yAxisId="cash" type="monotone" dataKey="cash" name="Cash Balance" stroke="#0891B2" strokeWidth={2} fill="url(#gradCashBriefing)" dot={{ r: 3, fill: '#fff', strokeWidth: 2, stroke: '#0891B2' }} />
                  <Line yAxisId="roi" type="monotone" dataKey="roi" name="ROI %" stroke="#7C3AED" strokeWidth={2} dot={{ r: 4, fill: '#fff', strokeWidth: 2.5, stroke: '#7C3AED' }} />
                  <ReferenceLine yAxisId="roi" y={0} stroke="#737373" strokeDasharray="3 3" />
                  <ReferenceLine yAxisId="cash" y={0} stroke="#737373" strokeDasharray="3 3" />
                </ComposedChart>
              </ResponsiveContainer>
              <ChartLegend items={[
                { name: 'Cash Balance', color: '#0891B2' },
                { name: 'ROI %', color: '#7C3AED' },
              ]} />
            </div>
          </div>
        </div>
      )}

      {/* ═══════ SENSITIVITY / STRESS TEST ═══════ */}
      {sensitivityAnalysis && (
        <div className="overflow-hidden" style={S.card}>
          <div className="p-5">
            <div className="mb-4">
              <h3 className="text-sm flex items-center gap-2" style={{ color: '#1A1A1A', fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
                <Shield className="w-4 h-4" style={{ color: '#7C3AED' }} /> Stress-Tested Scenarios
              </h3>
              <p className="text-xs mt-0.5" style={{ color: '#737373' }}>How your projected NAV holds up under adverse conditions</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-3">
              <div style={{ background: '#F5F5F5', border: '1px solid #E5E5E5', borderRadius: '4px', padding: '12px' }}>
                <div className="text-xs mb-1" style={{ color: '#525252', fontFamily: "'IBM Plex Mono', monospace" }}>Base Case NAV</div>
                <div className="text-lg font-bold" style={{ color: '#2563EB', fontVariantNumeric: 'tabular-nums', fontFamily: "'IBM Plex Mono', monospace" }}>{fmt(sensitivityAnalysis.base.nav)}</div>
              </div>
              {sensitivityAnalysis.scenarios.map((s, i) => (
                <div key={i} style={{ ...S.innerCard, padding: '12px' }}>
                  <div className="text-xs mb-1" style={{ color: '#525252', fontFamily: "'IBM Plex Mono', monospace" }}>{s.label}</div>
                  <div className="text-base font-bold" style={{ color: '#1A1A1A', fontVariantNumeric: 'tabular-nums', fontFamily: "'IBM Plex Mono', monospace" }}>{fmt(s.nav)}</div>
                  <div className="text-xs font-medium" style={{ color: s.color, fontFamily: "'IBM Plex Mono', monospace" }}>{s.delta >= 0 ? '+' : ''}{fmt(s.delta)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═══════ ANNUAL PROJECTIONS TABLE ═══════ */}
      <div className="overflow-hidden" style={S.card}>
        <div className="p-5">
          <div className="mb-4">
            <h3 className="text-sm flex items-center gap-2" style={{ color: '#1A1A1A', fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
              <BarChart3 className="w-4 h-4" style={{ color: '#333333' }} /> Annual Projections
            </h3>
            <p className="text-xs mt-0.5" style={{ color: '#737373' }}>Year-by-year breakdown of your mining operation</p>
          </div>
          <div className="overflow-x-auto" style={{ border: '1px solid #E5E5E5', borderRadius: '8px' }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: '#FCFCFC' }}>
                  {['Year','Miners','BTC Mined','Treasury','CapEx','OpEx','Cash','BTC Price','NAV','ROI'].map(h => (
                    <th key={h} className={h === 'Year' ? 'text-left py-3 px-3' : 'text-right py-3 px-3'} style={{ ...S.label, borderBottom: '1px solid #E5E5E5' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>{annualData.map((r, i) => (
                <tr key={r.year} className="transition-colors" style={{ borderBottom: i < annualData.length - 1 ? '1px solid #F5F5F5' : 'none' }}>
                  <td className="py-2.5 px-3 font-medium" style={{ color: '#333333', fontFamily: "'IBM Plex Mono', monospace" }}>{r.year}</td>
                  <td className="text-right py-2.5 px-3" style={S.tn('#333333')}>{r.minersEOY}</td>
                  <td className="text-right py-2.5 px-3" style={S.tn(S.C.gold)}>{r.btcMined.toFixed(4)}</td>
                  <td className="text-right py-2.5 px-3 font-medium" style={S.tn('#92700C')}>{r.treasuryBTC.toFixed(4)}</td>
                  <td className="text-right py-2.5 px-3" style={S.tn(S.C.red)}>{r.capexThisYear > 0 ? fmt(r.capexThisYear) : '—'}</td>
                  <td className="text-right py-2.5 px-3" style={S.tn(S.C.red)}>{r.opEx > 0 ? fmt(r.opEx) : '—'}</td>
                  <td className="text-right py-2.5 px-3" style={{ fontVariantNumeric: 'tabular-nums', fontFamily: "'IBM Plex Mono', monospace", color: r.cash >= 0 ? S.C.green : S.C.red }}>{fmt(r.cash)}</td>
                  <td className="text-right py-2.5 px-3" style={S.tn('#525252')}>{fmt(r.btcPrice)}</td>
                  <td className="text-right py-2.5 px-3" style={S.tn('#2563EB')}>{fmt(r.nav)}</td>
                  <td className="text-right py-2.5 px-3 font-semibold" style={S.tn('#7C3AED')}>{r.roi.toFixed(0)}%</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ═══════ EXPANSION TIMELINE ═══════ */}
      {strat.expansions.length > 0 && (
        <div className="overflow-hidden" style={S.card}>
          <div className="p-5">
            <div className="mb-4">
              <h3 className="text-sm flex items-center gap-2" style={{ color: '#1A1A1A', fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
                <Calendar className="w-4 h-4" style={{ color: '#16A34A' }} /> Expansion Timeline
              </h3>
              <p className="text-xs mt-0.5" style={{ color: '#737373' }}>Phased fleet growth schedule requiring precise execution</p>
            </div>
            <div className="relative">
              <div className="absolute top-6 left-0 right-0 h-0.5" style={{ background: '#E5E5E5' }} />
              <div className="flex justify-between relative">
                <div className="flex flex-col items-center" style={{ minWidth: '100px' }}>
                  <div className="w-3 h-3 rounded-full z-10" style={{ background: strat.color }} />
                  <div className="mt-2 text-center">
                    <div className="text-xs font-bold" style={{ color: strat.color, fontFamily: "'IBM Plex Mono', monospace" }}>Month 1</div>
                    <div className="text-xs" style={{ color: '#333333' }}>{strat.startingMiners} miners</div>
                    <div className="text-xs" style={{ color: '#525252', fontFamily: "'IBM Plex Mono', monospace" }}>{fmt(strat.startingMiners * strat.minerPrice)}</div>
                    <div className="text-[10px] mt-0.5" style={{ color: '#737373' }}>{strat.initialCapexIsSunk ? '(sunk cost)' : '(cash outlay)'}</div>
                  </div>
                </div>
                {strat.expansions.filter(e => e.miners > 0).map((exp, i) => {
                  const dotColor = exp.useLoan ? '#7C3AED' : '#16A34A';
                  return (
                    <div key={i} className="flex flex-col items-center" style={{ minWidth: '100px' }}>
                      <div className="w-3 h-3 rounded-full z-10" style={{ background: dotColor }} />
                      <div className="mt-2 text-center">
                        <div className="text-xs font-bold" style={{ color: dotColor, fontFamily: "'IBM Plex Mono', monospace" }}>Month {exp.month}</div>
                        <div className="text-xs" style={{ color: '#333333' }}>+{exp.miners} miners</div>
                        <div className="text-xs" style={{ color: '#525252', fontFamily: "'IBM Plex Mono', monospace" }}>{fmt(exp.miners * exp.minerPrice)}</div>
                        {exp.useLoan && <div className="text-[10px] mt-0.5" style={{ color: '#7C3AED', fontFamily: "'IBM Plex Mono', monospace" }}>+{fmt(exp.loanAmount)} loan</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════ OPERATIONAL REQUIREMENTS ═══════ */}
      <div className="overflow-hidden" style={{ background: '#FCFCFC', border: '1px solid #E5E5E5', borderRadius: '8px' }}>
        <div className="p-6">
          <div className="mb-2">
            <div style={{ fontSize: '12px', color: '#DC2626', textTransform: 'uppercase', letterSpacing: '2px', fontWeight: 600, marginBottom: '6px', fontFamily: "'IBM Plex Mono', monospace" }}>Execution Requirements</div>
            <h3 style={{ color: '#1A1A1A', fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: '18px' }}>What It Takes to Run This Strategy</h3>
            <p className="text-sm mt-1" style={{ color: '#525252', lineHeight: 1.6, fontWeight: 300, fontFamily: "'Outfit', sans-serif" }}>
              This strategy was modeled for your specific situation. Realizing these projections requires ongoing, active management across multiple operational domains. Here's what's involved:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
            <OpsTask
              icon={Wrench}
              title="Hardware Procurement"
              description={`Source and negotiate ${totalMiners} ASICs across ${1 + expansionCount} purchase cycles. Validate specifications, manage shipping logistics, and coordinate deployment windows.`}
              frequency={`${1 + expansionCount} procurement events over ${timeHorizon} years`}
              color="#92700C"
            />
            <OpsTask
              icon={Zap}
              title="Power & Hosting"
              description={`Secure hosting at $${electricityCost}/kWh. Monitor uptime SLAs, negotiate rate renewals, and manage facility relationships as fleet scales.`}
              frequency="Ongoing, checked monthly"
              color="#92700C"
            />
            <OpsTask
              icon={Settings}
              title="Fleet Monitoring"
              description="Track hashrate performance, troubleshoot hardware failures, manage firmware updates, and coordinate RMA processes for failing units."
              frequency="Daily monitoring required"
              color="#7C3AED"
            />
            <OpsTask
              icon={Calendar}
              title="Expansion Execution"
              description={`Time ${expansionCount} hardware purchase${expansionCount !== 1 ? 's' : ''} around halving cycles and difficulty adjustments. Coordinate deployment with hosting capacity.`}
              frequency={`${expansionCount} critical expansion windows`}
              color="#2563EB"
            />
            {hasLoans && (
              <OpsTask
                icon={DollarSign}
                title="Loan & Cash Flow Management"
                description="Service loan payments on schedule, monitor cash buffer levels, coordinate BTC liquidation timing for operational expenses."
                frequency="Monthly, multiple payment streams"
                color="#DC2626"
              />
            )}
            <OpsTask
              icon={Clock}
              title="Lifecycle & Resale"
              description={`Plan fleet refresh cycles as hardware reaches end-of-life. Coordinate resale timing, manage depreciation schedules, and source replacements.`}
              frequency={`${Math.ceil(timeHorizon / 5)} refresh cycles projected`}
              color="#0891B2"
            />
            <OpsTask
              icon={AlertTriangle}
              title="Network & Difficulty"
              description="Monitor Bitcoin network difficulty adjustments (~every 2 weeks), evaluate impact on profitability, adjust strategy if sustained adverse trends emerge."
              frequency="Bi-weekly assessment"
              color="#16A34A"
            />
            <OpsTask
              icon={Shield}
              title="Tax & Compliance"
              description="Track CapEx depreciation schedules (MACRS/bonus), document OpEx deductions, maintain LLC records, and coordinate with your CPA."
              frequency="Quarterly reporting, annual filing"
              color="#333333"
            />
            <OpsTask
              icon={Users}
              title="Pool & Vendor Management"
              description="Evaluate mining pool performance and fees, manage hosting contracts, coordinate with equipment vendors for warranty and support."
              frequency="Ongoing relationship management"
              color="#DB2777"
            />
          </div>

          <div style={{ marginTop: '20px', padding: '14px 18px', background: '#F5F5F5', border: '1px solid #E5E5E5', borderRadius: '8px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#DC2626' }}>
              This strategy requires active management across {hasLoans ? '9' : '8'} operational domains for {timeHorizon} years.
            </div>
            <div style={{ fontSize: '12px', color: '#333333', marginTop: '4px', lineHeight: 1.6, fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}>
              Missed expansion windows, suboptimal BTC sell timing, or hosting disruptions can significantly erode projected returns.
              Most LLC owners don't have the infrastructure or time to execute at this level consistently.
            </div>
          </div>
        </div>
      </div>

      {/* ═══════ DISCLAIMER ═══════ */}
      <div className="text-center py-4" style={{ color: '#737373', fontSize: '11px', lineHeight: 1.7, fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}>
        This briefing is for educational and planning purposes only. Bitcoin mining involves significant risks including hardware failure, network difficulty increases,
        BTC price volatility, and regulatory changes. Projections are based on modeled assumptions. Actual results will vary. Consult qualified financial and tax advisors before making investment decisions.
      </div>
    </div>
  );
}
